import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { EventCard, ModuleBase } from '@/components';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import { CategoryModel, ImageModel, LinkModel, ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import EventCalendarFilters from '@/components/events/EventCalendarFilters';

export type EventCardType = {
  name: string;
  type: string;
  location: string;
  eventDateTimeStart: string;
  eventDateTimeFinish: string;
  thumbnailImage: ImageModel;
  imageOrientation: string;
  reportUrl?: LinkModel;
  eventUrl?: {
    href: string;
  };
  directory?: CategoryModel[];
};

type EventCalendarModuleModel = ModuleBaseModel<
  {
    eventCards: EventCardType[];
    eventType: string[];
    date: string[];
    directories: CategoryModel[];
  } & ModuleBasePropsModel
>;

type CardsByMonthType = {
  id: string;
  cards: EventCardType[];
};

const EventCalendarModule = (data: EventCalendarModuleModel) => {
  const { props } = data;
  const [cards, setCards] = useState<EventCardType[]>([]);
  const [cardsByMonth, setCardsByMonth] = useState<CardsByMonthType[]>([]);
  const hasScrolledRef = useRef(false);

  const dateFilters = useMemo(() => {
    const dates: Array<{ label: string; id: string; children?: Array<{ label: string; id: string }> }> = [];
    let years: string[] = [];

    props.date.forEach((d) => {
      const year = dayjs(d).format('YYYY');
      if (years.indexOf(year) === -1) {
        years.push(year);
      }
    });

    years = years.sort((a, b) => parseInt(b) - parseInt(a));
    const datesAsc = [...props.date].sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());

    years.forEach((y) => {
      const months: Array<{ label: string; id: string }> = [];

      datesAsc.forEach((dateAsc) => {
        if (dayjs(dateAsc).format('YYYY') === y) {
          months.push({
            label: dayjs(dateAsc).format('MMM YYYY'),
            id: dateAsc,
          });
        }
      });
      dates.push({
        label: y,
        id: y,
        children: months,
      });
    });

    return dates;
  }, [props.date]);

  const directoryFilters = useMemo(() => {
    return props.directories.map((d) => ({
      label: d.categoryDisplayName,
      value: d.categoryCodeName,
    }));
  }, [props.directories]);

  const filter = useCallback(
    (filterData: { eventDateFilter: string[]; eventTypeFilter: string | null; directoryFilter: string[] | null }) => {
      const { eventDateFilter, eventTypeFilter, directoryFilter } = filterData;
      let filteredCards = [...props.eventCards];

      if (eventDateFilter && eventDateFilter.length > 0) {
        filteredCards = filteredCards.filter((card) => {
          return eventDateFilter.some(
            (p) =>
              dayjs(p).isSame(dayjs(new Date(card.eventDateTimeStart).toISOString()), 'month') ||
              (p.length === 4 && dayjs(p).isSame(dayjs(new Date(card.eventDateTimeStart).toISOString()), 'year')),
          );
        });
      } else {
        filteredCards = filteredCards.filter((card) =>
          dayjs(new Date().toISOString()).isSame(dayjs(new Date(card.eventDateTimeStart).toISOString()), 'year'),
        );
      }

      if (eventTypeFilter != null && eventTypeFilter.length > 0) {
        filteredCards = filteredCards.filter((card) => card.type === eventTypeFilter);
      }

      if (directoryFilter != null && directoryFilter.length > 0) {
        const dirFilteredCards: EventCardType[] = [];
        filteredCards.forEach((card) => {
          if (card.directory && card.directory.length > 0) {
            card.directory.forEach((dir) => {
              if (directoryFilter.some((p) => dir.categoryCodeName === p)) {
                dirFilteredCards.push(card);
              }
            });
          }
        });
        filteredCards = dirFilteredCards;
      }

      setCards(filteredCards);
    },
    [props.eventCards],
  );

  const breakCardsIntoMonths = () => {
    const grouped: CardsByMonthType[] = [];
    cards.forEach((card) => {
      const monthYear = dayjs(card.eventDateTimeStart).format('YYYY-MM-01');
      const lookupMonth = grouped.findIndex((x) => x.id === monthYear);
      if (lookupMonth === -1) {
        grouped.push({ id: monthYear, cards: [card] });
      } else {
        grouped[lookupMonth].cards.push(card);
      }
    });
    setCardsByMonth(grouped);
  };

  const getMonthTitle = (id: string) => {
    return dayjs(id).format('MMMM YYYY');
  };

  const goto = (idName: string) => {
    const element = document.getElementById(idName);
    if (element) {
      const headerOffset = 170;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    filter({
      eventDateFilter: [],
      eventTypeFilter: null,
      directoryFilter: null,
    });
  }, [filter]);

  useEffect(() => {
    if (cardsByMonth.length > 0 && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      const timeoutId = setTimeout(() => {
        goto(dayjs(new Date().toISOString()).format('YYYY-MM-01'));
      }, 2000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [cardsByMonth]);

  useEffect(() => {
    breakCardsIntoMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards]);

  if (props.eventCards.length === 0) {
    return null;
  }

  return (
    <ModuleBase data={data} noPadding>
      <EventCalendarFilters
        filters={{
          eventTypes: [...props.eventType],
          directories: directoryFilters,
          date: dateFilters,
        }}
        onFilterChange={filter}
      />

      <div className="container mx-auto lg:pr-10">
        {cardsByMonth.map((monthOfCards) => (
          <div key={monthOfCards.id} className="mb-10">
            <div className="px-2.5 py-2.5">
              <div id={monthOfCards.id}>
                <h1 className="text-2xl font-bold md:text-[32px]">{getMonthTitle(monthOfCards.id)}</h1>
              </div>
            </div>
            <div className="flex flex-wrap sm:-mx-2.5">
              {monthOfCards.cards.map((card, index) => (
                <div
                  key={index}
                  className="xxl:w-1/5 border-gray-2 w-full border-b py-6 last:border-b-0 sm:w-1/2 sm:border-b-0 sm:px-2.5 md:w-1/2 lg:w-1/3 lg:px-3.5 lg:py-2.5 xl:w-1/4"
                >
                  <EventCard eventCard={card} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ModuleBase>
  );
};

export default EventCalendarModule;
